import { useState, useCallback, useEffect, useMemo, useRef } from "react";
import useIsMobile from "../client/hooks/useIsMobile";
import { ensurePfmDefaultLayout, getActiveLayout, useAction, useQuery } from "wasp/client/operations";
import { Building2, Loader2, Layers, ChevronDown, Upload } from "lucide-react";
import AddPayoutModal from "./AddPayoutModal";
import { Button } from "../client/components/ui/button";
import { useActiveLayout, compactSlots } from "../dashboard/widgets/useActiveLayout";
import { withDefaultLayoutFallback } from "../dashboard/widgets/registry";
import { useLayoutsLibrary } from "../dashboard/widgets/useLayoutsLibrary";
import DashboardLayout from "../dashboard/widgets/DashboardLayout";
import EditModeBar from "../dashboard/widgets/EditModeBar";
import AddWidgetPicker, { TOPBAR_WIDGET_KEYS } from "../dashboard/widgets/AddWidgetPicker";
import LayoutLibraryModal from "../dashboard/widgets/LayoutLibraryModal";
import TopbarSlotRenderer from "../dashboard/widgets/TopbarSlotRenderer";
import TopbarFilterBar from "../dashboard/widgets/TopbarFilterBar";
import type { LayoutSnapshot, SlotPlacement, WidgetKey } from "../dashboard/widgets/types";
import useTranslation from "../client/hooks/useTranslation";
import { useWidgetData } from "../dashboard/widgets/WidgetDataContext";
import {
  getDefaultDateRange,
  getDefaultAccountFilter,
  getDefaultStrategyIds,
  serializeDateRange,
  serializeAccountFilter,
  serializeStrategyIds,
} from "../custom-pages/topbarDefaults";
import { useToast } from "../client/hooks/use-toast";

function layoutFromRows(rows: any[]): LayoutSnapshot {
  const allSlots: SlotPlacement[] = rows.flatMap((row: any) =>
    (row.slots ?? []).map((slot: any) => ({
      id: slot.id,
      widgetKey: slot.widgetKey as WidgetKey,
      colStart: slot.colStart,
      rowStart: row.order + slot.rowStart - 1,
      colSpan: slot.colSpan,
      rowSpan: slot.rowSpan,
      ...(slot.notebookNoteId ? { notebookNoteId: slot.notebookNoteId } : {}),
      ...((slot.placement) ? { placement: slot.placement as "grid" | "topbar" } : {}),
      ...(slot.defaultFilterValue != null ? { defaultFilterValue: slot.defaultFilterValue } : {}),
      ...(slot.viewMode != null ? { viewMode: slot.viewMode as "chart" | "table" } : {}),
    }))
  );
  const topbar = allSlots.filter((s) => s.placement === "topbar");
  const grid = allSlots.filter((s) => s.placement !== "topbar");
  return { slots: [...topbar, ...compactSlots(grid)] };
}


export default function PfmLayoutShell({ isFreePlan }: { isFreePlan: boolean }) {
  const t = useTranslation();
  const { toast } = useToast();
  const {
    setShowPlanModal,
    setShowImportModal,
    dateRange, setDateRange,
    accountFilter, setAccountFilter,
    strategyIds, setStrategyIds,
    accounts, groups, strategies,
    pfmTemplateFilter, setPfmTemplateFilter,
    accountStatusFilter, setAccountStatusFilter,
  } = useWidgetData();
  const [isEditMode, setIsEditMode] = useState(false);
  const [isDuplicating, setIsDuplicating] = useState(false);
  const [showAddPayoutModal, setShowAddPayoutModal] = useState(false);
  const [layoutLibraryOpen, setLayoutLibraryOpen] = useState(false);
  const isDuplicatingRef = useRef(false);
  const [pickerState, setPickerState] = useState<{
    open: boolean; col: number; row: number; maxCol: number; context?: "topbar" | "grid";
  }>({ open: false, col: 1, row: 1, maxCol: 6 });

  const ensureFn = useAction(ensurePfmDefaultLayout);
  const ensureCalledRef = useRef(false);
  const pfmScope = useMemo(() => ({ scope: "pfm" as const }), []);
  const { data: activeLayoutData, refetch: refetchActiveLayout } = useQuery(getActiveLayout, pfmScope);
  const { layouts, activeLayout, duplicate, activate, refetchLayouts } = useLayoutsLibrary("pfm");

  useEffect(() => {
    if (ensureCalledRef.current) return;
    ensureCalledRef.current = true;
    ensureFn(undefined).then(() => refetchLayouts()).catch(() => {});
  }, []);

  const serverSnapshot: LayoutSnapshot = useMemo(() =>
    withDefaultLayoutFallback(
      activeLayoutData
        ? layoutFromRows((activeLayoutData as any).rows ?? [])
        : { slots: [] },
      "pfm",
    ),
  [activeLayoutData]);

  // Sync filter state from topbar slot defaults once layout loads
  const filterInitialized = useRef(false);
  useEffect(() => {
    if (filterInitialized.current || !activeLayoutData) return;
    filterInitialized.current = true;
    const slots = serverSnapshot.slots;
    setDateRange(getDefaultDateRange(slots));
    setAccountFilter(getDefaultAccountFilter(slots));
    setStrategyIds(getDefaultStrategyIds(slots));
  }, [activeLayoutData]);

  const activeLayoutId = (activeLayoutData as any)?.id ?? null;
  const isActiveReadOnly = (activeLayout as any)?.isReadOnly ?? false;

  const { layout, addWidget, removeWidget, setWidgetSize, moveWidget, swapWidgets, flushSave, compactAndFlushSave, updateSlotDefaultFilterValue, updateSlotViewMode } =
    useActiveLayout(serverSnapshot, activeLayoutId);

  const topbarSlots = useMemo(
    () => layout.slots.filter((s) => s.placement === "topbar"),
    [layout.slots]
  );

  const handleAddWidgetClick = useCallback((colStart: number, rowStart: number, maxColSpan: number) => {
    setPickerState({ open: true, col: colStart, row: rowStart, maxCol: maxColSpan, context: "grid" });
  }, []);

  const handlePick = useCallback((key: WidgetKey) => {
    if (TOPBAR_WIDGET_KEYS.has(key)) {
      addWidget(key, 1, 0, 1, 1, "topbar");
    } else {
      const maxOccupiedRow = layout.slots
        .filter((s) => s.placement !== "topbar")
        .reduce((m, s) => Math.max(m, s.rowStart + s.rowSpan - 1), 0);
      addWidget(key, 1, maxOccupiedRow + 1);
    }
    setPickerState((p) => ({ ...p, open: false }));
  }, [addWidget, layout.slots]);

  const handleDone = useCallback(async () => {
    await compactAndFlushSave();
    setIsEditMode(false);
  }, [compactAndFlushSave]);

  const handleReset = useCallback(async () => {
    await refetchActiveLayout();
    setIsEditMode(false);
  }, [refetchActiveLayout]);

  const isMobile = useIsMobile();

  const pfmTopbarExtraActions = (
    <>
      {/* Import CSV de l'historique prop firm : la modale était montée dans
          PfmView sans aucun déclencheur, donc inatteignable. */}
      {!isEditMode && (
        <Button
          variant="outline"
          size="sm"
          className="text-xs h-8 gap-1"
          onClick={() => setShowImportModal(true)}
        >
          <Upload className="h-3.5 w-3.5 shrink-0" />
          <span className="truncate">{t.propFirmManager.importHistory}</span>
        </Button>
      )}
      <Button
        variant="outline"
        size="sm"
        className="text-xs h-8 gap-1 max-w-[140px]"
        onClick={() => setLayoutLibraryOpen(true)}
      >
        <Layers className="h-3.5 w-3.5 shrink-0" />
        <span className="truncate">{(activeLayout as any)?.name ?? t.dashboard.layouts.menuTitle}</span>
        <ChevronDown className="h-3 w-3 shrink-0" />
      </Button>
      {!isEditMode && (
        <Button
          size="sm"
          variant="outline"
          className="text-xs h-8 min-w-[5rem]"
          disabled={isDuplicating}
          onClick={async () => {
            if (isFreePlan) {
              setShowPlanModal(true);
              return;
            }
            if (isActiveReadOnly) {
              if (isDuplicatingRef.current) return;
              isDuplicatingRef.current = true;
              setIsDuplicating(true);
              let success = false;
              try {
                const created = await duplicate((activeLayout as any).id, t.dashboard.layouts.newLayout);
                if (created) {
                  const activated = await activate((created as any).id);
                  if (activated) success = true;
                }
              } finally {
                setIsDuplicating(false);
                isDuplicatingRef.current = false;
              }
              if (!success) return;
              await refetchActiveLayout();
            }
            setIsEditMode(true);
          }}
        >
          {isDuplicating ? (
            <><Loader2 className="h-3 w-3 animate-spin" />{t.dashboard.customize}</>
          ) : (
            t.dashboard.customize
          )}
        </Button>
      )}
    </>
  );

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-3 flex-wrap">
        <Building2 className="h-5 w-5 text-primary shrink-0" />
        <h1 className="text-xl font-bold mr-1">{t.propFirmManager.title}</h1>
        <div className="flex items-center gap-2 ml-auto flex-wrap">
          {isEditMode && !isActiveReadOnly ? (
            topbarSlots.map((slot) => (
              <TopbarSlotRenderer
                key={slot.id}
                slot={slot}
                isEditMode={true}
                dateRange={dateRange}
                accountFilter={accountFilter}
                strategyIds={strategyIds}
                accounts={accounts}
                groups={groups}
                strategies={strategies}
                onDateRangeChange={setDateRange}
                onAccountFilterChange={setAccountFilter}
                onStrategyIdsChange={setStrategyIds}
                onAddTrade={() => {}}
                onAddPayout={() => setShowAddPayoutModal(true)}
                onRemove={() => { removeWidget(slot.id); flushSave(); }}
                onPin={(value) => {
                  updateSlotDefaultFilterValue(slot.id, value);
                  flushSave();
                  toast({ description: value === null ? t.pages.unpinDefault : t.pages.pinAsDefaultConfirm, duration: 1500 });
                }}
                pfmTemplateFilter={pfmTemplateFilter}
                onPfmTemplateFilterChange={setPfmTemplateFilter}
                accountStatusFilter={accountStatusFilter}
                onAccountStatusFilterChange={setAccountStatusFilter}
              />
            ))
          ) : (
            <TopbarFilterBar
              slots={topbarSlots}
              dateRange={dateRange}
              accountFilter={accountFilter}
              strategyIds={strategyIds}
              accounts={accounts}
              groups={groups}
              strategies={strategies}
              onDateRangeChange={setDateRange}
              onAccountFilterChange={setAccountFilter}
              onStrategyIdsChange={setStrategyIds}
              onAddTrade={() => {}}
              onAddPayout={() => setShowAddPayoutModal(true)}
              onRemove={() => {}}
              onPin={undefined}
              pfmTemplateFilter={pfmTemplateFilter}
              onPfmTemplateFilterChange={setPfmTemplateFilter}
              accountStatusFilter={accountStatusFilter}
              onAccountStatusFilterChange={setAccountStatusFilter}
              extraActions={pfmTopbarExtraActions}
            />
          )}
          {isEditMode && !isActiveReadOnly && (
            <Button
              size="sm"
              variant="outline"
              className="text-xs h-8"
              onClick={() => setPickerState({ open: true, col: 1, row: 1, maxCol: 6, context: "topbar" })}
            >
              {t.pages.topbarAdd}
            </Button>
          )}
          {/* En édition, les slots sont rendus inline (pas via TopbarFilterBar), donc
              extraActions doit être placé ici. En lecture, c'est TopbarFilterBar qui le
              rend — les deux modes sont exclusifs, jamais de double rendu. */}
          {isEditMode && !isActiveReadOnly && pfmTopbarExtraActions}
          <LayoutLibraryModal
            open={layoutLibraryOpen}
            onOpenChange={setLayoutLibraryOpen}
            scope="pfm"
            isFreePlan={isFreePlan}
            onUpgradeClick={() => setShowPlanModal(true)}
            activeLayoutId={(activeLayout as any)?.id ?? null}
            onNewLayoutActivated={() => refetchLayouts()}
            onBeforeActivate={flushSave}
          />
        </div>
      </div>

      {isEditMode && (
        <EditModeBar onDone={handleDone} onReset={handleReset} />
      )}

      <DashboardLayout
        layout={layout}
        isEditMode={isEditMode && !isActiveReadOnly}
        isMobile={isMobile}
        onAddWidgetClick={handleAddWidgetClick}
        onRemoveWidget={removeWidget}
        onWidgetSizeChange={setWidgetSize}
        onMoveWidget={moveWidget}
        onSwapWidgets={swapWidgets}
        onUpdateSlotConfig={updateSlotDefaultFilterValue}
        onUpdateSlotViewMode={updateSlotViewMode}
      />

      <AddWidgetPicker
        open={pickerState.open}
        onOpenChange={(v) => setPickerState((p) => ({ ...p, open: v }))}
        layout={layout}
        maxColSpan={pickerState.maxCol}
        rowStart={pickerState.row}
        colStart={pickerState.col}
        scope="pfm"
        initialCategory={pickerState.context === "topbar" ? "topbar" : undefined}
        isFreePlan={isFreePlan}
        onPick={handlePick}
        onUpgradeClick={() => setShowPlanModal(true)}
      />

      <AddPayoutModal
        open={showAddPayoutModal}
        onClose={() => setShowAddPayoutModal(false)}
        accounts={accounts}
        onSuccess={() => setShowAddPayoutModal(false)}
      />
    </div>
  );
}
